import "./App.css";
import {
  CognitoAuthProvider,
  AppLayout,
  AccountMenu,
  LogoutPage,
  LoggedOutPage,
  ProtectedRoute,
  CallbackPage,
  AppFooter,
  AppBar,
  NavRail,
  useThemeSwitcher,
  getDefaultAccountMenuItems,
} from "common-fe";
import { createBrandTheme, Logo, colors } from "brand";
import AccountSettings from "./pages/AccountSettings";
import Organizations from "./pages/Organizations";
import OrganizationEdit from "./pages/OrganizationEdit";
import OrganizationMembers from "./pages/OrganizationMembers";
import Home from "./pages/Home";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { ThemeProvider, useColorScheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import HomeIcon from "@mui/icons-material/Home";
import SettingsIcon from "@mui/icons-material/Settings";
import GroupIcon from "@mui/icons-material/Group";
import LogoutIcon from "@mui/icons-material/Logout";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import { useCallback } from "react";
import { useTranslation } from "i18n-fe";

function AppContent({ cognitoAuthConfig }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("common");
  const { mode, toggleTheme } = useThemeSwitcher();

  const handleAuthenticated = useCallback(
    (targetPath) => {
      navigate(targetPath, { replace: true });
    },
    [navigate]
  );

  const theme = createBrandTheme();

  const BrandLogo = () => (
    <Box
      onClick={() => navigate("/")}
      sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
    >
      <Logo
        style={{
          paddingLeft: "8px",
          height: 13,
          width: "auto",
          minHeight: 13,
          minWidth: 13,
          maxHeight: 13,
          maxWidth: "none",
          marginRight: 16,
          flexShrink: 0,
          flexGrow: 0,
        }}
      />
      <Typography variant="h2" sx={{ fontSize: "1.1rem" }}>
        {t("app.accountCenterTitle")}
      </Typography>
    </Box>
  );

  const navItems = [
    {
      icon: <HomeIcon />,
      text: t("navigation.appsLink"),
      onClick: () => navigate("/"),
    },
    {
      icon: <GroupIcon />,
      text: t("navigation.organizationsLink"),
      onClick: () => navigate("/orgs"),
    }
  ];

  const accountMenuItems = getDefaultAccountMenuItems({ 
    t, 
    mode, 
    toggleTheme, 
    navigate,
    cognitoConfig: {
      clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
      cognitoDomain: import.meta.env.VITE_COGNITO_AUTH_DOMAIN,
      logoutUri: import.meta.env.VITE_COGNITO_POST_LOGOUT_REDIRECT_URI,
    }
  });

  return (
    <CognitoAuthProvider
      cognitoAuthConfig={cognitoAuthConfig}
      location={location}
      onAuthenticated={handleAuthenticated}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppLayout
          appBar={
            <AppBar
              sx={{ backgroundColor: colors.darkGrey, color: "#ffffff" }}
              rightContent={<AccountMenu menuItems={accountMenuItems} />}
              brandLogo={<BrandLogo />}
            />
          }
          navRail={
            <NavRail
              navItems={navItems}
              backgroundColor={colors.darkGrey}
              color="#ffffff"
            />
          }
          
          appFooterEnabled={true}
          appFooter={
            <AppFooter
              sx={{ backgroundColor: colors.darkGrey, color: "#ffffff" }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2">
                  {t("app.copyrightNotice", { year: new Date().getFullYear() })}
                </Typography>
              </Box>
            </AppFooter>
          }
          brandLogo={<BrandLogo />}
          appBarRightContent={<AccountMenu menuItems={accountMenuItems} />}
        >
          <Routes>
            <Route
              path="/callback"
              element={
                <CallbackPage navigateFunction={navigate} location={location} />
              }
            />
            <Route
              path="/logout"
              element={<LogoutPage navigateFunction={navigate} />}
            />
            <Route path="/logged-out" element={<LoggedOutPage />} />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <AccountSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orgs"
              element={
                <ProtectedRoute>
                  <Organizations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orgs/:id"
              element={
                <ProtectedRoute>
                  <OrganizationEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orgs/:id/members"
              element={
                <ProtectedRoute>
                  <OrganizationMembers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              }
            />
          </Routes>
        </AppLayout>
      </ThemeProvider>
    </CognitoAuthProvider>
  );
}

function App({ cognitoAuthConfig }) {
  const theme = createBrandTheme();

  return (
    <ThemeProvider theme={theme} defaultMode="light">
      <CssBaseline />
      <AppContent cognitoAuthConfig={cognitoAuthConfig} />
    </ThemeProvider>
  );
}

export default App;
